// src/admin/components/reports/RevenueReport.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams, Link } from 'react-router-dom';
import {
  RiMoneyDollarCircleLine,
  RiCalendarLine,
  RiFileChartLine,
  RiAlertLine,
  RiDownloadLine,
  RiUserLine,
  RiRefreshLine,
  RiArrowLeftLine,
  RiArrowRightLine,
  RiFilter2Line,
  RiSaveLine,
  RiPrinterLine
} from 'react-icons/ri';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  getRevenueSummary,
  formatCurrency,
  formatDate,
  TIME_PERIOD_OPTIONS,
  getCurrentMonthStartEnd,
  getCurrentYearStartEnd,
  getCurrentQuarterStartEnd,
  getMonthStartEnd
} from '../../../models/reportModel';
import RevenueChart from './charts/RevenueChart';
import * as XLSX from 'xlsx';

const RevenueReport = () => {
  const { t, i18n } = useTranslation();
  const { isDarkMode } = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();

  // Check if the current language is RTL
  const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

  // Select the appropriate arrow icon based on RTL
  const BackArrow = isRTL ? RiArrowRightLine : RiArrowLeftLine;

  // Get date params from URL or use the current YEAR as default (not just the current
  // month) — a sale/payment made today should still show up by default instead of the
  // report looking "empty/broken" whenever nothing happened to land in the last 30 days.
  const defaultDates = getCurrentYearStartEnd();
  const startDate = searchParams.get('startDate') || defaultDates.startDate;
  const endDate = searchParams.get('endDate') || defaultDates.endDate;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('current_year');
  const [customDates, setCustomDates] = useState({
    startDate,
    endDate
  });
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    // Set the initial period based on the URL params
    const detectPeriod = () => {
      const currentMonth = getCurrentMonthStartEnd();
      const lastMonth = getMonthStartEnd(
        new Date().getMonth() === 0 ? new Date().getFullYear() - 1 : new Date().getFullYear(),
        new Date().getMonth() === 0 ? 12 : new Date().getMonth()
      );
      const currentQuarter = getCurrentQuarterStartEnd();
      const currentYear = getCurrentYearStartEnd();

      if (startDate === currentMonth.startDate && endDate === currentMonth.endDate) {
        return 'current_month';
      } else if (startDate === lastMonth.startDate && endDate === lastMonth.endDate) {
        return 'last_month';
      } else if (startDate === currentQuarter.startDate && endDate === currentQuarter.endDate) {
        return 'current_quarter';
      } else if (startDate === currentYear.startDate && endDate === currentYear.endDate) {
        return 'current_year';
      } else {
        return 'custom';
      }
    };

    setSelectedPeriod(detectPeriod());
  }, [startDate, endDate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('Fetching revenue data with dates:', { startDate, endDate });

        if (!startDate || !endDate) {
          console.warn('Start date or end date is missing, skipping fetch');
          setLoading(false);
          return;
        }

        const response = await getRevenueSummary(startDate, endDate);
        setData(response);
      } catch (err) {
        console.error('Error fetching revenue data:', err);
        setError(err.message || t('failed_to_load_revenue_data') || 'Failed to load revenue data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, t]);

  const handlePeriodChange = (e) => {
    const period = e.target.value;
    setSelectedPeriod(period);

    let newDates = {};

    switch (period) {
      case 'current_month':
        newDates = getCurrentMonthStartEnd();
        break;
      case 'last_month':
        const now = new Date();
        const lastMonth = now.getMonth() === 0 ? 12 : now.getMonth();
        const year = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
        newDates = getMonthStartEnd(year, lastMonth);
        break;
      case 'current_quarter':
        newDates = getCurrentQuarterStartEnd();
        break;
      case 'current_year':
        newDates = getCurrentYearStartEnd();
        break;
      case 'custom':
        // Keep the current dates for custom
        newDates = { startDate, endDate };
        break;
      default:
        newDates = getCurrentMonthStartEnd();
    }

    setCustomDates(newDates);

    if (period !== 'custom') {
      // Update URL params
      setSearchParams({
        startDate: newDates.startDate,
        endDate: newDates.endDate
      });
    }
  };

  const handleCustomDateSelection = (update) => {
    const [start, end] = update;

    const formatLocal = (date) => {
      if (!date) return '';
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    setCustomDates({
      startDate: formatLocal(start),
      endDate: formatLocal(end)
    });
  };

  const applyCustomDates = () => {
    setSearchParams({
      startDate: customDates.startDate,
      endDate: customDates.endDate
    });
    setFilterOpen(false);
  };

  const handleRefresh = () => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await getRevenueSummary(startDate, endDate);
        setData(response);
      } catch (err) {
        console.error('Error fetching revenue data:', err);
        setError(err.message || t('failed_to_load_revenue_data') || 'Failed to load revenue data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  };

  // Handle Print Functionality
  const handlePrint = () => {
    const originalTitle = document.title;
    const reportTitle = `${t('revenue_report')} - ${formatDate(startDate)} ${t('to')} ${formatDate(endDate)}`;
    document.title = reportTitle;

    // Create a new window for printing
    const printWindow = window.open('', '_blank');

    // Define CSS styles for the print window
    const printStyles = `
      @media print {
        @page { size: landscape; }
        body {
          font-family: Arial, sans-serif;
          padding: 20px;
          direction: ${isRTL ? 'rtl' : 'ltr'};
        }
        h1 {
          font-size: 18px;
          margin-bottom: 10px;
          text-align: center;
        }
        .period {
          font-size: 14px;
          text-align: center;
          margin-bottom: 20px;
          color: #666;
        }
        .summary {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-bottom: 20px;
          justify-content: space-between;
        }
        .summary-item {
          border: 1px solid #ddd;
          padding: 15px;
          border-radius: 8px;
          width: calc(25% - 20px);
          box-sizing: border-box;
        }
        .summary-item h4 {
          margin: 0 0 5px 0;
          font-size: 14px;
          color: #666;
        }
        .summary-item p {
          margin: 0;
          font-size: 18px;
          font-weight: bold;
        }
        .summary-item small {
          display: block;
          margin-top: 5px;
          font-size: 12px;
          color: #666;
        }
        table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 20px;
        }
        th, td {
          border: 1px solid #ddd;
          padding: 8px;
          text-align: ${isRTL ? 'right' : 'left'};
        }
        th {
          background-color: #f2f2f2;
          font-weight: bold;
        }
        .amount {
          text-align: ${isRTL ? 'left' : 'right'};
          font-weight: bold;
        }
        .section-title {
          margin: 15px 0;
          font-size: 16px;
          font-weight: bold;
        }
        .footer {
          margin-top: 20px;
          text-align: center;
          font-size: 12px;
          color: #666;
        }
      }
    `;

    // Generate the HTML content for printing
    const printContent = `
      <!DOCTYPE html>
      <html lang="${i18n.language}" dir="${isRTL ? 'rtl' : 'ltr'}">
        <head>
          <meta charset="UTF-8">
          <title>${reportTitle}</title>
          <style>${printStyles}</style>
        </head>
        <body>
          <h1>${reportTitle}</h1>
          <div class="period">${formatDate(startDate)} - ${formatDate(endDate)}</div>
          
          <div class="summary">
            <div class="summary-item">
              <h4>${t('total_revenue')}</h4>
              <p>${formatCurrency(data.summary?.total_revenue || 0)}</p>
              <small>${t('total_payments_count', { count: data.summary?.total_payments || 0 })}</small>
            </div>
            <div class="summary-item">
              <h4>${t('rent_revenue')}</h4>
              <p>${formatCurrency(data.summary?.rent_revenue || 0)}</p>
              <small>${t('percent_of_total', { percent: Math.round(((data.summary?.rent_revenue || 0) / (data.summary?.total_revenue || 1)) * 100) })}</small>
            </div>
            <div class="summary-item">
              <h4>${t('service_revenue')}</h4>
              <p>${formatCurrency(data.summary?.service_revenue || 0)}</p>
              <small>${t('percent_of_total', { percent: Math.round(((data.summary?.service_revenue || 0) / (data.summary?.total_revenue || 1)) * 100) })}</small>
            </div>
            <div class="summary-item">
              <h4>${t('unique_tenants')}</h4>
              <p>${data.summary?.unique_tenants || 0}</p>
              <small>${t('avg_per_tenant', { amount: formatCurrency((data.summary?.total_revenue || 0) / (data.summary?.unique_tenants || 1)) })}</small>
            </div>
          </div>
          
          <h3 class="section-title">${t('revenue_by_building')}</h3>
          <table>
            <thead>
              <tr>
                <th>${t('building')}</th>
                <th class="amount">${t('total_revenue')}</th>
                <th class="amount">${t('rent_revenue')}</th>
                <th class="amount">${t('service_revenue')}</th>
                <th class="amount">${t('tenants')}</th>
              </tr>
            </thead>
            <tbody>
              ${data.buildingRevenue && data.buildingRevenue.length > 0 ?
        data.buildingRevenue.map(building => `
                  <tr>
                    <td>${building.buildingName}</td>
                    <td class="amount">${formatCurrency(building.total_revenue)}</td>
                    <td class="amount">${formatCurrency(building.rent_revenue)}</td>
                    <td class="amount">${formatCurrency(building.service_revenue)}</td>
                    <td class="amount">${building.tenant_count}</td>
                  </tr>
                `).join('') :
        `<tr><td colspan="5" style="text-align: center">${t('no_building_revenue_data')}</td></tr>`
      }
            </tbody>
          </table>
          
          <h3 class="section-title">${t('revenue_by_owner')}</h3>
          <table>
            <thead>
              <tr>
                <th>${t('owner')}</th>
                <th class="amount">${t('total_revenue')}</th>
                <th class="amount">${t('rent_revenue')}</th>
                <th class="amount">${t('service_revenue')}</th>
                <th class="amount">${t('apartments')}</th>
              </tr>
            </thead>
            <tbody>
              ${data.ownerRevenue && data.ownerRevenue.length > 0 ?
        data.ownerRevenue.map(owner => `
                  <tr>
                    <td>${owner.ownerName}</td>
                    <td class="amount">${formatCurrency(owner.total_revenue)}</td>
                    <td class="amount">${formatCurrency(owner.rent_revenue)}</td>
                    <td class="amount">${formatCurrency(owner.service_revenue)}</td>
                    <td class="amount">${owner.apartment_count}</td>
                  </tr>
                `).join('') :
        `<tr><td colspan="5" style="text-align: center">${t('no_owner_revenue_data')}</td></tr>`
      }
            </tbody>
          </table>
          
          <div class="footer">
            ${t('printed_on', { date: new Date().toLocaleDateString(i18n.language) }) || `Printed on ${new Date().toLocaleDateString(i18n.language)}`}
          </div>
        </body>
      </html>
    `;

    // Write to the new window and print
    printWindow.document.open();
    printWindow.document.write(printContent);
    printWindow.document.close();

    // Wait for content to load before printing
    printWindow.onload = () => {
      printWindow.print();
      printWindow.onafterprint = () => {
        printWindow.close();
        document.title = originalTitle;
      };
    };
  };

  // Handle Excel Export Functionality
  const handleExportToExcel = () => {
    try {
      setExporting(true);

      // Create workbook
      const wb = XLSX.utils.book_new();

      // Format summary data
      const summaryData = [
        {
          [t('category')]: t('total_revenue'),
          [t('amount')]: data.summary?.total_revenue || 0,
          [t('details')]: t('total_payments_count', { count: data.summary?.total_payments || 0 })
        },
        {
          [t('category')]: t('rent_revenue'),
          [t('amount')]: data.summary?.rent_revenue || 0,
          [t('details')]: t('percent_of_total', { percent: Math.round(((data.summary?.rent_revenue || 0) / (data.summary?.total_revenue || 1)) * 100) })
        },
        {
          [t('category')]: t('service_revenue'),
          [t('amount')]: data.summary?.service_revenue || 0,
          [t('details')]: t('percent_of_total', { percent: Math.round(((data.summary?.service_revenue || 0) / (data.summary?.total_revenue || 1)) * 100) })
        },
        {
          [t('category')]: t('unique_tenants'),
          [t('amount')]: data.summary?.unique_tenants || 0,
          [t('details')]: t('avg_per_tenant', { amount: formatCurrency((data.summary?.total_revenue || 0) / (data.summary?.unique_tenants || 1)) })
        }
      ];

      // Format building revenue data
      const buildingData = data.buildingRevenue?.map(building => ({
        [t('building')]: building.buildingName,
        [t('total_revenue')]: building.total_revenue,
        [t('rent_revenue')]: building.rent_revenue,
        [t('service_revenue')]: building.service_revenue,
        [t('tenants')]: building.tenant_count
      })) || [];

      // Format owner revenue data
      const ownerData = data.ownerRevenue?.map(owner => ({
        [t('owner')]: owner.ownerName,
        [t('total_revenue')]: owner.total_revenue,
        [t('rent_revenue')]: owner.rent_revenue,
        [t('service_revenue')]: owner.service_revenue,
        [t('apartments')]: owner.apartment_count
      })) || [];

      // Create worksheets
      const summaryWS = XLSX.utils.json_to_sheet(summaryData);
      const buildingWS = XLSX.utils.json_to_sheet(buildingData);
      const ownerWS = XLSX.utils.json_to_sheet(ownerData);

      // Set column widths
      const summaryColumnWidths = [
        { wch: 20 }, // Category
        { wch: 15 }, // Amount
        { wch: 30 }  // Details
      ];

      const revenueColumnWidths = [
        { wch: 30 }, // Building/Owner
        { wch: 15 }, // Total Revenue
        { wch: 15 }, // Rent Revenue
        { wch: 15 }, // Service Revenue
        { wch: 15 }  // Tenants/Apartments
      ];

      summaryWS['!cols'] = summaryColumnWidths;
      buildingWS['!cols'] = revenueColumnWidths;
      ownerWS['!cols'] = revenueColumnWidths;

      // Add worksheets to workbook
      XLSX.utils.book_append_sheet(wb, summaryWS, t('summary') || 'Summary');
      XLSX.utils.book_append_sheet(wb, buildingWS, t('buildings') || 'Buildings');
      XLSX.utils.book_append_sheet(wb, ownerWS, t('owners') || 'Owners');

      // Generate filename with date range
      const fileName = `${t('revenue_report').replace(/\s+/g, '_')}_${startDate}_${endDate}.xlsx`;

      // Export to file
      XLSX.writeFile(wb, fileName);
    } catch (err) {
      console.error('Error exporting to Excel:', err);
      setError(t('failed_to_export') || 'Failed to export to Excel');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'} className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
            <RiMoneyDollarCircleLine className={`${isRTL ? 'ml-3' : 'mr-3'} ${isDarkMode ? 'text-green-400' : 'text-green-600'}`} />
            {t('revenue_report') || 'Revenue Report'}
          </h1>
          <p className={`mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
            {formatDate(startDate)} - {formatDate(endDate)}
          </p>
        </div>

        <div className={`flex items-center ${isRTL ? 'space-x-3 space-x-reverse' : 'space-x-3'}`}>
          <button
            onClick={() => setFilterOpen(!filterOpen)}
            className={`px-3 py-2 rounded-lg ${isDarkMode
              ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600 border-gray-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300'
              } border flex items-center`}
          >
            <RiFilter2Line className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
            {t('filter') || 'Filter'}
          </button>

          <button
            onClick={handleRefresh}
            className={`px-3 py-2 rounded-lg ${isDarkMode
              ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              } flex items-center`}
            disabled={loading}
          >
            <RiRefreshLine className={`${isRTL ? 'ml-2' : 'mr-2'} ${loading ? 'animate-spin' : ''}`} />
            {loading ? (t('loading') || 'Loading...') : (t('refresh') || 'Refresh')}
          </button>

          <Link
            to="/admin/reports"
            className={`px-3 py-2 rounded-lg ${isDarkMode
              ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              } flex items-center`}
          >
            <BackArrow className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
            {t('back') || 'Back'}
          </Link>
        </div>
      </div>

      {/* Filter Panel */}
      {filterOpen && (
        <div className={`p-4 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
          }`}>
          <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {t('filter_report') || 'Filter Report'}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t('time_period') || 'Time Period'}
              </label>
              <select
                value={selectedPeriod}
                onChange={handlePeriodChange}
                className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                  }`}
              >
                {TIME_PERIOD_OPTIONS.map(option => (
                  <option key={option.value} value={option.value}>
                    {t(option.value) || option.label}
                  </option>
                ))}
              </select>
            </div>

            {selectedPeriod === 'custom' && (
              <div className="md:col-span-1">
                <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  {t('select_date_range') || 'Select Date Range'}
                </label>
                <div className="relative">
                  <DatePicker
                    selectsRange={true}
                    startDate={customDates.startDate ? new Date(customDates.startDate) : null}
                    endDate={customDates.endDate ? new Date(customDates.endDate) : null}
                    onChange={handleCustomDateSelection}
                    dateFormat="MM/dd/yyyy"
                    placeholderText="Select start and end date"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    portalId="root-portal"
                    className={`w-full p-2.5 rounded-lg border ${isDarkMode ? 'bg-zinc-800 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
                      }`}
                  />
                  <RiCalendarLine className={`absolute ${isRTL ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none`} />
                </div>
              </div>
            )}
          </div>

          <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} ${isRTL ? 'space-x-3 space-x-reverse' : 'space-x-3'}`}>
            <button
              onClick={() => setFilterOpen(false)}
              className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              {t('cancel') || 'Cancel'}
            </button>

            <button
              onClick={applyCustomDates}
              className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-zinc-500 text-white hover:bg-red-600'
                } flex items-center`}
              disabled={!customDates.startDate || !customDates.endDate}
            >
              <RiFilter2Line className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('apply_filter') || 'Apply Filter'}
            </button>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center p-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-t-2 border-red-500"></div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-red-900/20 border-red-800 text-red-400' : 'bg-zinc-50 border-red-200 text-red-700'
          } border flex items-center`}>
          <RiAlertLine className={`${isRTL ? 'ml-2' : 'mr-2'} text-xl`} />
          {error}
        </div>
      )}

      {/* Report Content */}
      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('total_revenue') || 'Total Revenue'}
                  </p>
                  <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(data.summary?.total_revenue || 0)}
                  </h3>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('total_payments_count', { count: data.summary?.total_payments || 0 }) ||
                      `${data.summary?.total_payments || 0} payments`}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'
                  }`}>
                  <RiMoneyDollarCircleLine size={24} />
                </div>
              </div>
            </div>

            {(data.summary?.sale_commission_expense || 0) > 0 && (
              <div className={`p-5 rounded-xl shadow border-2 ${isDarkMode ? 'bg-gray-750 border-green-900/50' : 'bg-white border-green-200'
                }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                      {t('net_revenue_after_commission') || 'Net Revenue (after commission)'}
                    </p>
                    <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>
                      {formatCurrency(data.summary?.net_revenue_after_commission || 0)}
                    </h3>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                      {t('net_revenue_after_commission_hint') || 'Total Revenue minus sale commission paid out. Does not include other expenses (maintenance, payroll, etc.) — not tracked in this system yet.'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-full ${isDarkMode ? 'bg-green-900/20 text-green-400' : 'bg-green-100 text-green-600'
                    }`}>
                    <RiMoneyDollarCircleLine size={24} />
                  </div>
                </div>
              </div>
            )}

            <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('rent_revenue') || 'Rent Revenue'}
                  </p>
                  <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(data.summary?.rent_revenue || 0)}
                  </h3>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('percent_of_total', { percent: Math.round(((data.summary?.rent_revenue || 0) / (data.summary?.total_revenue || 1)) * 100) }) ||
                      `${Math.round(((data.summary?.rent_revenue || 0) / (data.summary?.total_revenue || 1)) * 100)}% of total`}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-zinc-100 text-red-600'
                  }`}>
                  <RiMoneyDollarCircleLine size={24} />
                </div>
              </div>
            </div>

            <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('service_revenue') || 'Service Revenue'}
                  </p>
                  <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(data.summary?.service_revenue || 0)}
                  </h3>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('percent_of_total', { percent: Math.round(((data.summary?.service_revenue || 0) / (data.summary?.total_revenue || 1)) * 100) }) ||
                      `${Math.round(((data.summary?.service_revenue || 0) / (data.summary?.total_revenue || 1)) * 100)}% of total`}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${isDarkMode ? 'bg-purple-900/20 text-purple-400' : 'bg-purple-100 text-purple-600'
                  }`}>
                  <RiMoneyDollarCircleLine size={24} />
                </div>
              </div>
            </div>

            <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
              }`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {t('sale_revenue') || 'Sale Revenue'}
                  </p>
                  <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {formatCurrency(data.summary?.sale_revenue || 0)}
                  </h3>
                  <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                    {`${Math.round(((data.summary?.sale_revenue || 0) / (data.summary?.total_revenue || 1)) * 100)}% of total`}
                  </p>
                </div>
                <div className={`p-3 rounded-full ${isDarkMode ? 'bg-orange-900/20 text-orange-400' : 'bg-orange-100 text-orange-600'
                  }`}>
                  <RiUserLine size={24} />
                </div>
              </div>
            </div>

            {(data.summary?.sale_commission_expense || 0) > 0 && (
              <div className={`p-5 rounded-xl shadow border-2 ${isDarkMode ? 'bg-gray-750 border-red-900/50' : 'bg-white border-red-200'
                }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                      {t('sale_commission_expense') || 'Sale Commission Paid'}
                    </p>
                    <h3 className={`text-2xl font-bold mt-1 ${isDarkMode ? 'text-red-400' : 'text-red-600'}`}>
                      − {formatCurrency(data.summary?.sale_commission_expense || 0)}
                    </h3>
                    <p className={`text-xs mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                      {t('sale_commission_expense_hint') ||
                        'Deducted for real estate agency commission on unit sales closed in this period — not netted out of Sale Revenue above.'}
                    </p>
                  </div>
                  <div className={`p-3 rounded-full ${isDarkMode ? 'bg-red-900/20 text-red-400' : 'bg-red-100 text-red-600'
                    }`}>
                    <RiMoneyDollarCircleLine size={24} />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Building Revenue */}
          <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
            }`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('revenue_by_building') || 'Revenue by Building'}
              </h3>
              <Link
                to={`/admin/reports/building-revenue?startDate=${startDate}&endDate=${endDate}`}
                className={`text-sm flex items-center ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'
                  }`}
              >
                {t('view_details') || 'View Details'}
              </Link>
            </div>

            <div className="overflow-x-auto">
              <table className={`w-full text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <thead className={`text-xs uppercase ${isDarkMode ? 'text-zinc-400 bg-zinc-800' : 'text-gray-500 bg-gray-100'}`}>
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left">{t('building') || 'Building'}</th>
                    <th scope="col" className={`px-4 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{t('total_revenue') || 'Total Revenue'}</th>
                    <th scope="col" className={`px-4 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{t('rent_revenue') || 'Rent Revenue'}</th>
                    <th scope="col" className={`px-4 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{t('service_revenue') || 'Service Revenue'}</th>
                    <th scope="col" className={`px-4 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{t('tenants') || 'Tenants'}</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {data.buildingRevenue && data.buildingRevenue.length > 0 ? (
                    data.buildingRevenue.map((building, index) => (
                      <tr
                        key={building.buildingId}
                        className={`${isDarkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-50'
                          } transition-colors duration-150`}
                      >
                        <td className="px-4 py-3 font-medium">{building.buildingName}</td>
                        <td className={`px-4 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{formatCurrency(building.total_revenue)}</td>
                        <td className={`px-4 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{formatCurrency(building.rent_revenue)}</td>
                        <td className={`px-4 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{formatCurrency(building.service_revenue)}</td>
                        <td className={`px-4 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{building.tenant_count}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="px-4 py-3 text-center">
                        {t('no_building_revenue_data') || 'No building revenue data available'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Owner Revenue */}
          <div className={`p-5 rounded-xl shadow ${isDarkMode ? 'bg-gray-750 border border-gray-700' : 'bg-white border border-gray-200'
            }`}>
            <div className="flex items-center justify-between mb-4">
              <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('revenue_by_owner') || 'Revenue by Owner'}
              </h3>
              <Link
                to={`/admin/reports/owner-revenue?startDate=${startDate}&endDate=${endDate}`}
                className={`text-sm flex items-center ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'
                  }`}
              >
                {t('view_details') || 'View Details'}
              </Link>
            </div>

            <div className="overflow-x-auto">
              <table className={`w-full text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                <thead className={`text-xs uppercase ${isDarkMode ? 'text-zinc-400 bg-zinc-800' : 'text-gray-500 bg-gray-100'}`}>
                  <tr>
                    <th scope="col" className="px-4 py-3 text-left">{t('owner') || 'Owner'}</th>
                    <th scope="col" className={`px-4 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{t('total_revenue') || 'Total Revenue'}</th>
                    <th scope="col" className={`px-4 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{t('sale_revenue') || 'Sale Revenue'}</th>
                    <th scope="col" className={`px-4 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{t('apartments') || 'Apartments'}</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                  {data.ownerRevenue && data.ownerRevenue.length > 0 ? (
                    data.ownerRevenue.map((owner, index) => (
                      <tr
                        key={owner.ownerId}
                        className={`${isDarkMode ? 'hover:bg-zinc-800' : 'hover:bg-gray-50'
                          } transition-colors duration-150`}
                      >
                        <td className="px-4 py-3 font-medium">{owner.ownerName}</td>
                        <td className={`px-4 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{formatCurrency(owner.total_revenue)}</td>
                        <td className={`px-4 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{formatCurrency(owner.sale_revenue)}</td>
                        <td className={`px-4 py-3 ${isRTL ? 'text-left' : 'text-right'}`}>{owner.apartment_count}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-4 py-3 text-center">
                        {t('no_owner_revenue_data') || 'No owner revenue data available'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Actions */}
          <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} ${isRTL ? 'space-x-3 space-x-reverse' : 'space-x-3'}`}>
            <button
              onClick={handlePrint}
              className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-zinc-800 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                } flex items-center`}
            >
              <RiPrinterLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
              {t('print_report') || 'Print Report'}
            </button>

            <button
              onClick={handleExportToExcel}
              className={`px-4 py-2 rounded-lg ${isDarkMode ? 'bg-red-600 text-white hover:bg-red-700' : 'bg-zinc-500 text-white hover:bg-red-600'
                } flex items-center`}
              disabled={exporting}
            >
              {exporting ? (
                <>
                  <div className={`h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin ${isRTL ? 'ml-2' : 'mr-2'}`}></div>
                  {t('exporting') || 'Exporting...'}
                </>
              ) : (
                <>
                  <RiDownloadLine className={`${isRTL ? 'ml-2' : 'mr-2'}`} />
                  {t('export_report') || 'Export Report'}
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default RevenueReport;